// Per spec/components.md §9. Single-row text-style event entry. Three CTA
// arms by ctaLabel + ctaUrl presence (P-037.15 D-115):
//
// - **No CTA** (both undefined) — collapses to a single-cell stack.
// - **Link CTA** (ctaLabel + ctaUrl both set) — title left + link right;
//   second row colspan=2. Link rendered with 14 px Helvetica bold `#c69b34`.
// - **Text CTA** (ctaLabel set, ctaUrl undefined) — title left + plain
//   black text right (no anchor; P-037.17 D-117 — was gold pre-fix).
//   Used for "Save the Date" per Banks's audit.
//
// Second-line composition: `${dateText}${audience ? ' · ' + audience : ''}`
// — no automatic "Open to:" prefix; consumers (events-list) decide whether
// to include it in the audience field. `space[1]` (4 px) top padding on
// the second row gives a tight pairing with the title.
//
// **Divider above each row (P-037.16 D-116).** `dividerAbove` defaults
// `true` — prepends a Divider primitive above the row content so every
// event in a list (including the first) carries a leading rule. The link
// arm passes `colSpan: 2` so the rule spans both title + CTA columns.
// Previously `dividerBelow` was the prop; the semantic flip closes a
// surfaced bug (first event lacked a rule above; trailing rules in 2-col
// rows were truncated to column 1).
//
// **Flush-top variant (P-037.29 post-Phase-Close §item A revision).**
// `dividerAboveFlushTop` — when true (and `dividerAbove` is also true),
// the prepended divider uses `paddingTop: 0, paddingBottom: tokens.
// space[4]` instead of the default symmetric `paddingY: tokens.space[4]`.
// Used by events-list for the first event row so the rule sits flush
// at the Block's leading edge with padding only below it. Banks: "the
// first 3 records line should be flush to the top of the block, then
// some padding to the first line of text." (verbatim quote from the
// §item A clarification — the events-list arm matches the grid-Block
// arm in helpers.ts).
//
// **D-033 paired-required relaxed (D-115).** `ctaUrl` was paired-required
// with `ctaLabel`; now optional. The text-CTA arm needs a way to express
// "label without URL." Consumers (events-list) drive the choice from a
// per-event enum constrained to known labels.

import { html } from '../render'
import { tokens } from '../tokens'
import { text } from '../primitives/text'
import { link } from '../primitives/link'
import { divider } from '../primitives/divider'
import { HtmlString } from '../types'
import { secondaryText } from './helpers'

interface EventRowBase {
  title: string
  dateText: string
  audience?: string
  dividerAbove?: boolean
  /** P-037.29 post-Phase-Close §item A revision — when true (paired with
   *  `dividerAbove: true`), the prepended Divider primitive renders with
   *  `paddingTop: 0, paddingBottom: tokens.space[4]` so the rule sits
   *  flush at the top of the row with padding only below. Used by
   *  events-list on the first event so the Block leads with a flush
   *  rule. Default: false (symmetric 16/16 padding). */
  dividerAboveFlushTop?: boolean
  /** Template-level link color from `RenderContext.linkColor` (P-037.18
   *  D-118). Used as the link-arm CTA color. Defaults to
   *  `tokens.color['link-default']`. The text-arm CTA color
   *  (`CTA_TEXT_COLOR`) is always black per D-117 — definitive label
   *  styling rather than link styling. */
  linkColor?: string
}

export type EventRowProps =
  | (EventRowBase & { ctaLabel?: undefined; ctaUrl?: undefined })
  | (EventRowBase & { ctaLabel: string; ctaUrl?: string })

const SECOND_LINE_STYLE = `padding-top: ${tokens.space[1]}px;`
// P-037.17 (D-117): text-arm CTAs (label without URL — e.g., "Save the
// Date") render in pure black #000000 so they read as definitive labels
// rather than disabled-looking link styling. (P-037.18 D-118: link-arm
// CTA color moved from inline #c69b34 to `props.linkColor` — template-
// level derivation from Header style.)
const CTA_TEXT_COLOR = '#000000'

export function eventRow(props: EventRowProps): HtmlString {
  const titleHtml = text({
    text: props.title,
    weight: tokens.type.weight.semibold,
  })
  const secondLineText =
    props.audience !== undefined ? `${props.dateText} · ${props.audience}` : props.dateText
  const secondLineHtml = secondaryText(secondLineText)
  const showDivider = props.dividerAbove ?? true
  // P-037.29 post-Phase-Close §item A revision — flush-top variant for
  // the first event row in events-list. Pass split paddingTop/Bottom
  // to the divider primitive when set; otherwise the primitive falls
  // back to the symmetric `paddingY` shorthand (byte-compat).
  const flushTop = props.dividerAboveFlushTop === true
  const flushPadProps = flushTop ? { paddingTop: 0, paddingBottom: tokens.space[4] } : {}

  if (props.ctaLabel === undefined) {
    const dividerHtml = showDivider ? divider(flushPadProps) : undefined
    // prettier-ignore
    return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">${dividerHtml}<tr><td valign="top">${titleHtml}</td></tr><tr><td style="${SECOND_LINE_STYLE}">${secondLineHtml}</td></tr></table>`
  }

  // Link arm (ctaUrl present): full anchor at the template-level link
  // color (P-037.18 D-118 — was inline #c69b34). Text arm (ctaUrl
  // undefined): plain inline `<span>` in black (P-037.17 D-117) —
  // definitive label styling rather than link styling.
  const ctaLinkColor = props.linkColor ?? tokens.color['link-default']
  // prettier-ignore
  const ctaHtml = props.ctaUrl !== undefined
    ? link({ text: props.ctaLabel, href: props.ctaUrl, color: ctaLinkColor, weight: tokens.type.weight.bold })
    : html`<span style="font-family: ${tokens.type.fontFamily}; color: ${CTA_TEXT_COLOR}; font-weight: ${tokens.type.weight.bold};">${props.ctaLabel}</span>`
  // 2-column row: divider needs colspan=2 to span both heading + CTA cells.
  const dividerHtml = showDivider ? divider({ colSpan: 2, ...flushPadProps }) : undefined
  // prettier-ignore
  return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">${dividerHtml}<tr><td valign="top">${titleHtml}</td><td valign="top" align="right">${ctaHtml}</td></tr><tr><td colspan="2" style="${SECOND_LINE_STYLE}">${secondLineHtml}</td></tr></table>`
}
